import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of, catchError, map } from 'rxjs';
import { HttpResponse } from '@angular/common/http';

import { SessionStorageService } from './session-storage.service';
import { environment } from '../../../environments/environment';
import { Student, BulkStudentEditPayload, StudentEnrollData } from '../models/student.model';
import { SchoolClass } from '../models/school-class.model';
import { ManagementUser } from '../models/management-user.model';
import { CustomerServiceBatchEnrollmentRecord, SchoolAdminBatchEnrollmentRecord } from '../models/batch-enrollment.model';

const REQ_OPTS = {
  withCredentials: true,
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
}

export interface BatchEnrollmentResponse {
  batchEnrollmentID: number;
  studentCount: number;
  enrolledStudentList: BatchEnrolledStudentResponse[];
}

export interface BatchEnrolledStudentResponse {
  teacherUsername: string;
  teacherEmail: string;
  studentFirstName: string;
  studentLastName: string;
  grade: number;
}

export interface LatestUndoableBatchEnrollmentResponse {
  batchEnrollmentID: number;
  createdAt: string;
  studentCount: number;
  enrollmentType: 'CUSTOMER_SERVICE' | 'SCHOOL_ADMIN';
  enrolledStudentList: BatchEnrolledStudentResponse[];
}

export interface TeacherSubscriptionCount {
  teacherID: number,
  diagnosticLicenseCount: number,
  interventionLicenseCount: number
}

@Injectable({
  providedIn: 'root'
})
export class EnrollmentService {

  getSchoolTeacherSubscriptionAvailability(schoolID: number) {
    return this.httpClient.get<TeacherSubscriptionCount[]>(
      `${environment.dataServicesURL}manage/enroll/school/${schoolID}/teachers/NumberUnassignedSubscriptions`,
      REQ_OPTS
    );
  }

  constructor(
    private httpClient: HttpClient,
    private sessionStorageService: SessionStorageService
  ) { }

  private getRemainingLicensesCount(teacherID?: number | null, schoolID?: number | null, subscriptionType?: string): Observable<number> {
    let currentUser: ManagementUser | null = this.sessionStorageService.userData();
    if (currentUser === null) return of(0) ;

    if (teacherID == null) {
      teacherID = currentUser.userID;
    }
    if (schoolID == null) {
      schoolID = currentUser.schoolID;
    }

    if (teacherID! > 0) {
      return this.httpClient.get<number>(environment.dataServicesURL + 'manage/enroll/teacher/' + teacherID + '/school/' + schoolID +'/NumberUnassignedSubscriptions/' + subscriptionType, REQ_OPTS);
    } else {
      return of(0);
    }
  }

  assignDiagnosticSubscriptionToStudents(studentIDs: number[], teacherID?: number) {
    let selectedTeacherID = teacherID || this.sessionStorageService.userData()!.userID ;
    let ids = studentIDs.join(',') ;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assigndiagnostics/${selectedTeacherID}/${ids}`, {}, REQ_OPTS) ;
  }

  assignFullProductSubscriptionToStudents(studentIDs: number[], teacherID?: number) {
    let selectedTeacherID = teacherID || this.sessionStorageService.userData()!.userID ;
    let ids = studentIDs.join(',') ;

    return this.httpClient.put(`${environment.dataServicesURL}manage/enroll/students/assignintervention/${selectedTeacherID}/${ids}`, {}, REQ_OPTS) ;
  }

  getGradeOptions() {
    return this.httpClient.get(environment.dataServicesURL + 'manage/enroll/options/grade', REQ_OPTS);
  }

  getClassOptions(): Observable<SchoolClass[]> {
    return this.httpClient.get<SchoolClass[]>(environment.dataServicesURL + 'manage/enroll/options/class', REQ_OPTS);
  }

  getGenderOptions() {
    return this.httpClient.get(environment.dataServicesURL + 'manage/enroll/options/gender', REQ_OPTS);
  }

  getAssessmentTypeOptions() {
    return this.httpClient.get(environment.dataServicesURL + 'manage/enroll/options/assessmentTypes', REQ_OPTS);
  }

  getCurriculumOptions(gradeEnum: number) {
    return this.httpClient.get(environment.dataServicesURL + 'manage/enroll/options/curriculum/' + gradeEnum, REQ_OPTS)
  }

  getRemainingDiagnosticLicensesCount(teacherID?: number | null, schoolID?: number | null): Observable<number> {
    return this.getRemainingLicensesCount(teacherID, schoolID, 'Diagnostic');
  }

  getRemainingInterventionLicensesCount(teacherID?: number | null, schoolID?: number | null): Observable<number> {
    return this.getRemainingLicensesCount(teacherID, schoolID, 'Intervention');
  }

  sendEnrollmentInfo(studentData: StudentEnrollData): any {
    return this.httpClient.post(environment.dataServicesURL + 'manage/enroll/student/add', studentData, REQ_OPTS)
      .pipe(
        catchError((err: any) => {
          return of(err);
        }),
        map((res: any) => {
          return res;
        })
      );
  }

  updateStudentInfo(studentData: Student): Observable<any> {
    return this.httpClient.put(environment.dataServicesURL + 'manage/enroll/student/' + studentData.userID, studentData, REQ_OPTS)
      .pipe(
        catchError((err: any) => {
          return of(err);
        })
      );
  }

  updateStudentsInfo(bulkStudentData: BulkStudentEditPayload): Observable<any> {
    return this.httpClient.put(environment.dataServicesURL + 'manage/enroll/students/bulk/edit', bulkStudentData, REQ_OPTS);
  }

  addStudentList(batchRecords: CustomerServiceBatchEnrollmentRecord[] | SchoolAdminBatchEnrollmentRecord[], type: string): Observable<BatchEnrollmentResponse> {
    return this.httpClient.post<BatchEnrollmentResponse>(`${environment.dataServicesURL}manage/enroll/student/add/${type}/list`, batchRecords, REQ_OPTS)
  }

  getLatestUndoableBatchEnrollment(): Observable<LatestUndoableBatchEnrollmentResponse | null> {
    return this.httpClient.get<LatestUndoableBatchEnrollmentResponse>(
      `${environment.dataServicesURL}manage/enroll/student/batch/latest-undoable`,
      REQ_OPTS,
    );
  }

  undoBatchEnrollment(batchEnrollmentID: number) {
    return this.httpClient.post(`${environment.dataServicesURL}manage/enroll/student/batch/${batchEnrollmentID}/undo`, {}, REQ_OPTS);
  }
}
